import { useState, useRef, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import api from "../services/api";
import { BASE_PATH } from "../basePath";

/** Dropdown controlado por React (sem depender do Bootstrap JS). */
function UserDropdown({ username, role, onAlterarSenha, onLogout }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  // Fecha ao clicar fora
  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  return (
    <div className="position-relative" ref={ref}>
      <button
        className="btn btn-outline-light btn-sm"
        onClick={() => setOpen((v) => !v)}
        type="button"
      >
        👤 {username}
        {role === "admin" && (
          <span className="badge bg-danger ms-1" style={{ fontSize: 9 }}>
            admin
          </span>
        )}
        <span className="ms-1">▾</span>
      </button>

      {open && (
        <ul
          className="dropdown-menu dropdown-menu-end show"
          style={{ position: "absolute", right: 0, top: "100%", zIndex: 9999, minWidth: 180 }}
        >
          <li>
            <button
              className="dropdown-item"
              onClick={() => { setOpen(false); onAlterarSenha(); }}
            >
              🔒 Alterar Senha
            </button>
          </li>
          <li><hr className="dropdown-divider" /></li>
          <li>
            <button
              className="dropdown-item text-danger"
              onClick={() => { setOpen(false); onLogout(); }}
            >
              ↩ Sair
            </button>
          </li>
        </ul>
      )}
    </div>
  );
}

export default function Navbar() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const role = localStorage.getItem("role");
  const username = localStorage.getItem("username");

  const [showAlterarSenha, setShowAlterarSenha] = useState(false);
  const [senhaForm, setSenhaForm] = useState({
    current_password: "",
    new_password: "",
    confirm_password: "",
  });
  const [senhaError, setSenhaError] = useState("");
  const [senhaOk, setSenhaOk] = useState(false);
  const [loading, setLoading] = useState(false);

  const logout = () => {
    localStorage.clear();
    navigate("/login");
  };

  const link = (to, label) => (
    <Link
      key={to}
      to={to}
      className={`nav-link ${pathname === to ? "active fw-bold" : ""}`}
    >
      {label}
    </Link>
  );

  const openAlterarSenha = () => {
    setSenhaForm({ current_password: "", new_password: "", confirm_password: "" });
    setSenhaError("");
    setSenhaOk(false);
    setShowAlterarSenha(true);
  };

  const submitAlterarSenha = async (e) => {
    e.preventDefault();
    setSenhaError("");
    setSenhaOk(false);

    if (senhaForm.new_password.length < 6) {
      setSenhaError("A nova senha deve ter pelo menos 6 caracteres.");
      return;
    }
    if (senhaForm.new_password !== senhaForm.confirm_password) {
      setSenhaError("As senhas não coincidem.");
      return;
    }
    if (senhaForm.new_password === senhaForm.current_password) {
      setSenhaError("A nova senha deve ser diferente da senha atual.");
      return;
    }

    setLoading(true);
    try {
      await api.post("/auth/change-password", {
        current_password: senhaForm.current_password,
        new_password: senhaForm.new_password,
      });
      setSenhaOk(true);
      setSenhaForm({ current_password: "", new_password: "", confirm_password: "" });
    } catch (err) {
      setSenhaError(
        err.response?.data?.detail || "Erro ao alterar senha. Tente novamente."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <nav className="navbar navbar-expand-lg navbar-dark bg-primary px-3">
        <span className="navbar-brand fw-bold">Comissões</span>

        {BASE_PATH === "/producao" && (
          <a href="/dashboard" className="btn btn-outline-light btn-sm me-3">
            ← Voltar para Gestão Documental
          </a>
        )}

        <div className="navbar-nav">
          {link("/calculo", "Calculadora")}
          {link("/historico", "Histórico")}
          {role === "admin" && link("/dashboard", "Dashboard")}
          {role === "admin" && link("/vendedores", "Vendedores")}
          {role === "admin" && link("/parametros", "Parâmetros")}
          {role === "admin" && link("/relatorios", "Relatórios")}
          {role === "admin" && link("/usuarios", "Usuários")}
        </div>

        <div className="ms-auto">
          <UserDropdown
            username={username}
            role={role}
            onAlterarSenha={openAlterarSenha}
            onLogout={logout}
          />
        </div>
      </nav>

      {/* ── Modal: Alterar própria senha ── */}
      {showAlterarSenha && (
        <div className="modal show d-block" style={{ background: "rgba(0,0,0,0.5)" }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header bg-primary text-white">
                <h5 className="modal-title">🔒 Alterar Senha</h5>
                <button
                  className="btn-close btn-close-white"
                  onClick={() => setShowAlterarSenha(false)}
                />
              </div>

              <form onSubmit={submitAlterarSenha}>
                <div className="modal-body row g-3">
                  {senhaError && (
                    <div className="col-12">
                      <div className="alert alert-danger py-2">{senhaError}</div>
                    </div>
                  )}
                  {senhaOk && (
                    <div className="col-12">
                      <div className="alert alert-success py-2">
                        ✅ Senha alterada com sucesso!
                      </div>
                    </div>
                  )}

                  <div className="col-12">
                    <label className="form-label fw-semibold">Senha Atual</label>
                    <input
                      type="password"
                      className="form-control"
                      required
                      autoFocus
                      value={senhaForm.current_password}
                      onChange={(e) =>
                        setSenhaForm({ ...senhaForm, current_password: e.target.value })
                      }
                    />
                  </div>

                  <div className="col-12">
                    <label className="form-label fw-semibold">Nova Senha</label>
                    <input
                      type="password"
                      className="form-control"
                      required
                      minLength={6}
                      placeholder="Mínimo 6 caracteres"
                      value={senhaForm.new_password}
                      onChange={(e) =>
                        setSenhaForm({ ...senhaForm, new_password: e.target.value })
                      }
                    />
                  </div>

                  <div className="col-12">
                    <label className="form-label fw-semibold">Confirmar Nova Senha</label>
                    <input
                      type="password"
                      className="form-control"
                      required
                      minLength={6}
                      placeholder="Repita a nova senha"
                      value={senhaForm.confirm_password}
                      onChange={(e) =>
                        setSenhaForm({ ...senhaForm, confirm_password: e.target.value })
                      }
                    />
                  </div>
                </div>

                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setShowAlterarSenha(false)}
                  >
                    Cancelar
                  </button>
                  <button type="submit" className="btn btn-primary" disabled={loading}>
                    {loading ? "Salvando..." : "Alterar Senha"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
